import type { FC } from 'react';
import { useCallback } from 'react';
import userPlaceholder from '~/common/assets/images/user-placeholder.png';
import type { UserOverview } from '~/common/types/user-overview';
import { resizeServingUrl } from '~/utils/image';
import * as Dialog from '@app/_components/dialog';
import { css, cva } from '@styled-system/css';
import { Paragraph, VStack } from '@styled-system/jsx';

interface Props {
  user: Pick<UserOverview, 'name' | 'thumbnailUrl'>;
  open: boolean;
  noOverlay?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const UnfollowConfirmModal: FC<Props> = ({
  open,
  onClose,
  onConfirm,
  user,
  noOverlay,
}) => {
  const onChangeOpen = useCallback(
    (e) => {
      if (!e.open) {
        onClose();
      }
    },
    [onClose]
  );
  if (!open) {
    return null;
  }

  const userThumbnailUrl = resizeServingUrl(user.thumbnailUrl, 96);

  return (
    <Dialog.Root
      open={open}
      id="unfollowConfirmModal"
      onOpenChange={onChangeOpen}
      lazyMount
      unmountOnExit
    >
      {!noOverlay && <Dialog.Backdrop background="rgba(0, 0, 0, 0.5)" />}

      <Dialog.Positioner>
        <Dialog.Content bg="surface_primary">
          <VStack
            gap="0"
            textAlign="center"
            padding="40px 0 0"
            justifyContent="center"
          >
            <img
              alt="ユーザー"
              className={css({
                backgroundColor: 'primary',
                border: '2px solid #fafafa',
                width: {
                  base: '72px',
                  lg: '96px',
                },
                height: {
                  base: '72px',
                  lg: '96px',
                },
                objectFit: 'cover',
                borderRadius: '50%',
              })}
              src={userThumbnailUrl || userPlaceholder}
            />
            <Paragraph
              padding="0 12px 0"
              marginTop="16px"
              fontSize="size_14"
              color="accent.1000"
              marginBottom="40px"
            >
              {user.name || 'ゲスト'}さんのフォローをやめますか？
            </Paragraph>
            <button
              onClick={onConfirm}
              className={baseOptionStyle({ color: 'red' })}
            >
              フォローをやめる
            </button>
            <button onClick={onClose} className={baseOptionStyle()}>
              キャンセル
            </button>
          </VStack>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};

const baseOptionStyle = cva({
  base: {
    lineHeight: '50px',
    margin: 'auto 0',
    fontSize: {
      base: 'size_14',
      lg: 'size_16',
    },
    borderTop: '1px solid #edeeef',
    display: 'block',
    width: '100%',
    _hover: {
      cursor: 'pointer',
      opacity: '0.7',
      backgroundColor: '#edeeef',
    },
  },
  variants: {
    color: {
      red: {
        color: 'red',
        fontWeight: '700',
      },
    },
  },
});
